import * as Model from "@effect/sql/Model";
import type { DateTime } from "effect";
import { Schema } from "effect";

/**
 * The lifecycle of one loop invocation.
 *
 * `pending` → dispatched but not yet running; `running` → the workflow is
 * stepping; `completed` / `stuck` / `canceled` are terminal; `superseded` marks
 * an invocation a newer one replaced.
 *
 * @since 1.0.0
 */
export const LoopInvocationStatus = Schema.Literal(
  "pending",
  "running",
  "completed",
  "stuck",
  "canceled",
  "superseded",
);
export type LoopInvocationStatus = Schema.Schema.Type<typeof LoopInvocationStatus>;

/**
 * Whether an invocation runs for real (`live`) or records what it would have
 * done without acting (`shadow`).
 *
 * @since 1.0.0
 */
export const LoopEngineMode = Schema.Literal("live", "shadow");
export type LoopEngineMode = Schema.Schema.Type<typeof LoopEngineMode>;

/**
 * The vendor coordinates a status projection reads an invocation back through.
 *
 * Every field is `NullOr`, never optional: the object round-trips through
 * `jsonb`, so an absent key and an explicit `null` must not be two different
 * states — the `LoopInput` reasoning.
 *
 * @since 1.0.0
 */
export const LoopCorrelation = Schema.Struct({
  orcaTaskId: Schema.NullOr(Schema.String),
  linearIssueIdentifier: Schema.NullOr(Schema.String),
  linearProjectId: Schema.NullOr(Schema.String),
  orcaProjectId: Schema.NullOr(Schema.String),
  prNumber: Schema.NullOr(Schema.Number),
});
export type LoopCorrelation = Schema.Schema.Type<typeof LoopCorrelation>;

/** The empty correlation — every coordinate absent. @since 1.0.0 */
export const EMPTY_LOOP_CORRELATION: LoopCorrelation = {
  orcaTaskId: null,
  linearIssueIdentifier: null,
  linearProjectId: null,
  orcaProjectId: null,
  prNumber: null,
};

/**
 * The interpreter's walk state as a structurally-opaque jsonb record.
 *
 * There is deliberately no interior schema here: the canonical `WalkState`
 * encoding is `@fm/cluster`'s `WalkStateCodec`, and restating it in `@fm/db`
 * would be the duplicate this repo's "never duplicate a model type" rule exists
 * to prevent — the `LoopDocument` precedent. `@fm/db` owns the COLUMN, not the
 * grammar, and never imports `@fm/loops` / `@fm/cluster`.
 *
 * @since 1.0.0
 */
export const LoopWalkStateEncoded = Schema.Record({ key: Schema.String, value: Schema.Unknown });
export type LoopWalkStateEncoded = Schema.Schema.Type<typeof LoopWalkStateEncoded>;

/**
 * The wire form of an outcome body: a small value inlined, or a reference to the
 * object store. The canonical `OutcomeBody` type is `@fm/loops`'; this is the
 * db column's opaque mirror.
 *
 * @since 1.0.0
 */
export const OutcomeBodyEncoded = Schema.Union(
  Schema.Struct({ _tag: Schema.Literal("Inline"), value: Schema.Unknown }),
  Schema.Struct({ _tag: Schema.Literal("Ref"), uri: Schema.String }),
);
export type OutcomeBodyEncoded = Schema.Schema.Type<typeof OutcomeBodyEncoded>;

/**
 * The columns `transitionStatus` may stamp as an invocation reaches a terminal
 * (or re-enters a pending) status, alongside the status change itself. Every
 * field is optional; drizzle drops an absent one from the `SET` list.
 *
 * `stuckReason` is explicitly nullable — `undefined` leaves the column alone,
 * `null` CLEARS it. The per-invocation mute writes `muted:<reason>` there and
 * clears it again on unmute, so the column needs both writes.
 *
 * @since 1.0.0
 */
export interface LoopInvocationTerminalPatch {
  readonly outcomeHead?: Readonly<Record<string, unknown>>;
  readonly outcomeBody?: OutcomeBodyEncoded;
  readonly stuckReason?: string | null;
  readonly pendingReason?: string;
  readonly concludedAt?: DateTime.Utc;
}

/**
 * Loop invocation model — one EXECUTION of a loop definition on the production
 * harness (OL-203).
 *
 * `customerId`, `loopId`, `parentInvocationId` and `createdByPersonId` are plain
 * strings with no foreign key: the referenced rows live across a schema boundary
 * (or would be deleted by a cascade that must not touch evidence), so every
 * reference is by id only.
 *
 * @since 1.0.0
 */
export class LoopInvocation extends Model.Class<LoopInvocation>("LoopInvocation")({
  id: Model.Generated(Schema.String),

  customerId: Schema.String,

  loopId: Schema.String,
  loopVersion: Schema.Number,
  loopSlug: Schema.String,

  inputs: Schema.Record({ key: Schema.String, value: Schema.Unknown }),

  mode: LoopEngineMode,
  status: LoopInvocationStatus,

  executionId: Schema.NullOr(Schema.String),

  parentInvocationId: Schema.NullOr(Schema.String),
  parentNode: Schema.NullOr(Schema.String),
  instanceId: Schema.NullOr(Schema.String),
  supersedesInvocationId: Schema.NullOr(Schema.String),

  parentDeferredToken: Schema.NullOr(Schema.String),

  triggerKey: Schema.NullOr(Schema.String),

  correlation: LoopCorrelation,

  state: Schema.NullOr(LoopWalkStateEncoded),
  stateSeq: Schema.Number,

  parkToken: Schema.NullOr(Schema.String),
  parkSeq: Schema.Number,

  outcomeHead: Schema.NullOr(Schema.Record({ key: Schema.String, value: Schema.Unknown })),
  outcomeBody: Schema.NullOr(OutcomeBodyEncoded),

  pendingReason: Schema.NullOr(Schema.String),
  stuckReason: Schema.NullOr(Schema.String),

  createdByPersonId: Schema.NullOr(Schema.String),

  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
  concludedAt: Schema.NullOr(Schema.DateTimeUtc),
}) {}
